import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from 'stores/authStore';
import { login } from 'utils/authUtils';
import { ROUTES, getReactAppAuthUrl } from 'utils/constants';
import Header from 'components/Header';
import Sidebar from 'components/Sidebar';
import LandingNavbar from './LandingNavbar';
import LandingHero from './LandingHero';
import LandingEcosystem from './LandingEcosystem';
import LandingDataFlow from './LandingDataFlow';
import LandingCTA from './LandingCTA';
import HomepageRefreshPopup from './HomepageRefreshPopup';
import { LandingMenuOpenContext } from './landingMenuOpenContext';
import { useLandingStickyStackOffsets } from './useLandingStickyStackOffsets';
import styles from './index.module.scss';

function Home() {
  const isAuth = useAuthStore((state) => state.isAuth);
  const isLoading = useAuthStore((state) => state.isLoading);
  const navigate = useNavigate();
  const [landingMenuOpen, setLandingMenuOpen] = useState(false);

  useLandingStickyStackOffsets(!isAuth);

  useEffect(() => {
    if (isAuth && !isLoading) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isAuth, isLoading, navigate]);

  const handleRegister = () => {
    const authBaseUrl = getReactAppAuthUrl();
    const redirectUri = `${window.location.origin}${ROUTES.DASHBOARD}`;
    const registerUrl = `${authBaseUrl}/auth/register?redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = registerUrl;
  };

  const handleLogin = () => {
    void login();
  };

  const handleScrollToServices = useCallback(() => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('hub-services');
    const narrow =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches;
    el?.scrollIntoView({
      behavior: narrow ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  if (isAuth && !isLoading) return null;

  return (
    <LandingMenuOpenContext.Provider value={landingMenuOpen}>
      <div className={styles.page}>
        {isAuth ? (
          <>
            <Sidebar />
            <Header />
          </>
        ) : (
          <LandingNavbar
            onLogin={handleLogin}
            onRegister={handleRegister}
            onScrollToServices={handleScrollToServices}
            isLoading={isLoading}
            menuOpen={landingMenuOpen}
            onMenuOpenChange={setLandingMenuOpen}
          />
        )}

        <main id="main-content">
          <LandingHero
            onLogin={handleLogin}
            onRegister={handleRegister}
            onScrollToServices={handleScrollToServices}
            isLoading={isLoading}
          />
          <section
            className={styles.ecosystemIntro}
            aria-labelledby="ecosystem-intro-heading"
          >
            <div className={styles.ecosystemIntroInner}>
              <h2
                id="ecosystem-intro-heading"
                className={styles.ecosystemTitle}
              >
                Built for simplification.
              </h2>
              <p className={styles.ecosystemSubtitle}>
                Simplify your work and amplify your business—every feature
                designed to automate your financial matters.
              </p>
            </div>
          </section>
          <LandingEcosystem />
          <LandingDataFlow />
          <section
            className={styles.freeOfferSection}
            aria-labelledby="free-offer-heading"
          >
            <div className={styles.freeOfferInner}>
              <p className={styles.freeOfferBadge}>Limited Time Offer</p>
              <h2 id="free-offer-heading" className={styles.freeOfferTitle}>
                Totally free for today
              </h2>
              <p className={styles.freeOfferSubtitle}>
                AbraFi is currently in active development. Register now to enjoy
                full access for free and lock in massive early-adopter discounts
                before we transition to a paid subscription.
              </p>
              <button
                className={styles.freeOfferButton}
                onClick={handleRegister}
                disabled={isLoading}
                type="button"
              >
                <UserPlus size={20} aria-hidden="true" />
                Get Started for Free Now
              </button>
              <p className={styles.freeOfferNote}>
                No credit card required. Lock in your early-adopter benefits
                today.
              </p>
            </div>
          </section>
          {!isAuth && (
            <LandingCTA
              onLogin={handleLogin}
              onRegister={handleRegister}
              isLoading={isLoading}
            />
          )}
        </main>
        {!isAuth && <HomepageRefreshPopup />}
      </div>
    </LandingMenuOpenContext.Provider>
  );
}

export default Home;
